import { Injectable } from '@angular/core';
import { OfflineService } from 'app/services/offline.service';
import { DocumentRestService, DocumentSyncService, Exercise, ExerciseProgramDocument } from 'fitforce-document-sync';
import { SyncLoggingService } from 'fitforce-logging';
import { ObserverService } from 'fitforce-offline-first';
import { Observable } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';

interface ExerciseProgramStore {
  exerciseProgram: ExerciseProgramDocument;
}

@Injectable({
  providedIn: 'root',
})
export class DocumentService extends DocumentSyncService {
  store: ExerciseProgramStore;

  constructor(documentRestService: DocumentRestService, offline: OfflineService, syncLog: SyncLoggingService,
              observerService: ObserverService) {
    super(documentRestService, offline, syncLog, observerService);
    this.syncKey = 'exerciseProgramDocumentService';
    this.store = {
      exerciseProgram: undefined,
    };

    this.restoreData();
  }

  /**
   * Syncs the exercise program with the server.
   */
  syncExerciseProgram() {
    return this.pushExerciseProgram();
  }

  /**
   * Restore the exercise program to the service store
   */
  restoreData() {
    return this.restoreExerciseProgram();
  }

  /**
   * Updates or inserts the provided exercise to the exercise program and immediately syncs to the server.
   * @param exercise exercise to update in the program
   */
  saveExercise(exercise: Exercise): Observable<void> {
    return this.offline
      .getRecord<ExerciseProgramDocument>(ExerciseProgramDocument, { uuid: this.store.exerciseProgram.uuid })
      .pipe(
        concatMap(exerciseProgram => {
          const index = exerciseProgram.document.exercises.findIndex(ex => ex.id === exercise.id);
          if (index > -1) {
            exerciseProgram.document.exercises[index] = exercise;
          } else {
            exerciseProgram.document.exercises.push(exercise);
          }

          exerciseProgram.document.version++;
          exerciseProgram.markDirty();
          return this.offline.save(ExerciseProgramDocument, exerciseProgram);
        }),
        concatMap(() => {
          return this.syncExerciseProgram();
        }),
        concatMap(() => this.restoreExerciseProgram()),
      );
  }

  /**
   * Removes the provided exercise and any references from the exercise program document and immediately pushes to the server.
   * @param exercise exercise to remove
   */
  removeExercise(exercise: Exercise): Observable<void> {
    return this.offline
      .getRecord<ExerciseProgramDocument>(ExerciseProgramDocument, { uuid: this.store.exerciseProgram.uuid })
      .pipe(
        concatMap(exerciseProgram => {
          // remove exercise from exercises array
          exerciseProgram.document.exercises = exerciseProgram.document.exercises.filter(ex => ex.id !== exercise.id);

          // remove reference to exercise from workout types
          exerciseProgram.document.workout_types.forEach(workoutType => {
            workoutType.tiers.forEach(tier => {
              tier.rows.forEach(row => {
                if (row.exercises.includes(exercise.id)) {
                  row.exercises = row.exercises.filter(id => id !== exercise.id);
                }
              });
            });
          });

          exerciseProgram.document.version++;
          exerciseProgram.markDirty();
          return this.offline.save(ExerciseProgramDocument, exerciseProgram);
        }),
        concatMap(() => {
          return this.syncExerciseProgram();
        }),
        concatMap(() => this.restoreExerciseProgram()),
      );
  }

  /**
   * Restores exercise program document to service state.
   */
  private restoreExerciseProgram(): Observable<void> {
    return this.offline.getAll<ExerciseProgramDocument>(ExerciseProgramDocument).pipe(
      map(exercisePrograms => {
        this.store.exerciseProgram = exercisePrograms || exercisePrograms.length > 0 ? exercisePrograms[0] : null;

        return null;
      }),
    );
  }
}
